import {
  type InvalidateQueryFilters,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { produce } from 'immer';
import { StatusCode, REACT_QUERY_KEYS } from '@hungryroot/rest';
import {
  patchApiV2CustomersCustomerIdTags,
  type PatchedCustomerTagList as TagPayload,
} from '@hungryroot/rest/api/v2/customers/[id]/tags/patch';
import { type CustomerTagGroupResponse } from '@hungryroot/tags';

export { TagPayload };

interface Options {
  invalidationKeys?: InvalidateQueryFilters[];
}

function usePatchCustomerTagHook(options: Options = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { data: TagPayload[] }) => {
      const { data } = params;
      const res = await patchApiV2CustomersCustomerIdTags({
        requestBody: data,
      });

      if (res.status !== StatusCode.SuccessOK) {
        throw new Error(JSON.stringify(res.data, null, 2));
      }
      return res.data;
    },
    onMutate: async (newTag) => {
      await queryClient.cancelQueries({
        queryKey: [REACT_QUERY_KEYS.CUSTOMER_TAG_GROUP],
      });
      const existingCustomerTags =
        queryClient.getQueriesData<CustomerTagGroupResponse>({
          queryKey: [REACT_QUERY_KEYS.CUSTOMER_TAG_GROUP],
        });

      existingCustomerTags.forEach(([queryKey, results]) => {
        const next = results
          ? produce(results, (draft) => {
              const tagMap = draft.results.reduce(
                (seed, tag) => {
                  seed[tag.tag] = tag;
                  return seed;
                },
                {} as Record<
                  string,
                  CustomerTagGroupResponse['results'][number]
                >
              );

              newTag.data.forEach((result) => {
                const match = tagMap[result.tag];

                if (match === undefined) {
                  draft.results.push(result);
                  return;
                }

                match.preference = result.preference;
              });
            })
          : {
              results: newTag.data,
            };
        queryClient.setQueryData(queryKey, next);
      });
      return { existingCustomerTags };
    },
    onError: (
      err,
      newTag,
      context:
        | {
            existingCustomerTags: unknown;
          }
        | undefined
    ) => {
      queryClient.setQueryData(
        [REACT_QUERY_KEYS.CUSTOMER_TAG_GROUP],
        context?.existingCustomerTags
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [REACT_QUERY_KEYS.CUSTOMER_TAG_GROUP],
      });

      if (options.invalidationKeys) {
        options.invalidationKeys.forEach((invalidate) => {
          queryClient.invalidateQueries(invalidate);
        });
      }
    },
  });
}

export function usePatchCustomerTagSelf(opts: Options = {}) {
  return usePatchCustomerTagHook(opts);
}
